import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { tbqFunctions } from './firebase-config';
import { httpsCallable } from 'firebase/functions';

const FilterComponent = ({
  filterText,
  onFilter,
  isAdmin,
  locations,
  selectedLocation,
  onLocationChange,
  onLocationClear,
  selectedAttendingFilter,
  onAttendingFilterChange,
  selectedGenderFilter,
  onGenderFilterChange,
}) => (
  <div className="filters-row">
    <input
      type="text"
      placeholder="Search by Name, Email, or Quiz ID"
      aria-label="Search Input"
      value={filterText}
      onChange={onFilter}
      className="filter-input"
    />
    {isAdmin ? (
      <>
        <div className="location-filter-wrap">
          <input
            type="text"
            list="attempts-location-options"
            className="filter-select"
            value={selectedLocation}
            onChange={onLocationChange}
            aria-label="Filter by location"
            placeholder="All Locations"
          />
          {selectedLocation ? (
            <button
              type="button"
              className="btn-clear-location"
              onClick={onLocationClear}
              aria-label="Clear location filter"
              title="Clear"
            >
              x
            </button>
          ) : null}
          <datalist id="attempts-location-options">
            {locations.map((location) => (
              <option key={location} value={location} />
            ))}
          </datalist>
        </div>
        <select
          className="filter-select"
          value={selectedAttendingFilter}
          onChange={onAttendingFilterChange}
          aria-label="Filter by attending mode"
        >
          <option value="">All Users</option>
          <option value="attending">Attending at Location</option>
          <option value="online">Online</option>
        </select>
        <select
          className="filter-select"
          value={selectedGenderFilter}
          onChange={onGenderFilterChange}
          aria-label="Filter by gender"
        >
          <option value="">All Genders</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </select>
      </>
    ) : null}
  </div>
);

const convertToCSV = (objArray, columns) => {
  const headers = columns.map(column => column.name).join(',') + '\r\n';
  const rows = objArray.map(obj => {
    return columns.map(column => {
      const value = typeof column.selector === 'function' ? column.selector(obj) : obj[column.selector];
      const stringValue = value ? value.toString().replace(/"/g, '""') : '';
      return `"${stringValue}"`;
    }).join(',');
  }).join('\r\n');

  return headers + rows;
};

const downloadCSV = (csv, filename) => {
  const csvFile = new Blob([csv], { type: "text/csv" });
  const downloadLink = document.createElement("a");
  downloadLink.download = filename;
  downloadLink.href = window.URL.createObjectURL(csvFile);
  downloadLink.style.display = "none";
  document.body.appendChild(downloadLink);
  downloadLink.click();
};

const customStyles = {
  headCells: {
    style: {
      backgroundColor: '#183D24',
      color: '#ffffff',
      fontWeight: '600',
      fontSize: '13px',
    },
  },
  cells: {
    style: {
      fontSize: '13px',
      padding: '8px 12px',
    },
  },
  rows: {
    highlightOnHoverStyle: {
      backgroundColor: '#e6f0ea',
    },
  },
};

const AttemptsTab = ({ userCenter, isAdmin }) => {
  const [attempts, setAttempts] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [allQuizTitles, setAllQuizTitles] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedAttendingFilter, setSelectedAttendingFilter] = useState('');
  const [selectedGenderFilter, setSelectedGenderFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isExporting, setIsExporting] = useState(false);
  const [sortField, setSortField] = useState('grandTotal');
  const [sortDirection, setSortDirection] = useState('desc');
  const mountedRef = useRef(false);
  const listAttemptScores = useMemo(
    () => httpsCallable(tbqFunctions, 'listAttemptScores'),
    []
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filterText), 300);
    return () => clearTimeout(timer);
  }, [filterText]);

  const loadScoresPage = useCallback(async (page, pageSize, searchText = debouncedSearch) => {
    if (!isAdmin && !userCenter) return;

    setLoading(true);
    try {
      const response = await listAttemptScores({
        location: isAdmin ? selectedLocation : '',
        attendingMode: selectedAttendingFilter || '',
        gender: selectedGenderFilter || '',
        search: searchText || '',
        page,
        pageSize,
        sortField,
        sortDirection,
      });

      const data = response.data || {};
      const rows = Array.isArray(data.rows) ? data.rows : [];
      const quizColumns = Array.isArray(data.quizColumns) ? data.quizColumns : [];
      setAllQuizTitles(quizColumns);
      setAttempts(rows);

      const reportedTotal = Number(
        data.filteredTotalRows !== undefined ? data.filteredTotalRows : data.totalRows || 0
      );
      if (Number.isFinite(reportedTotal) && reportedTotal >= 0) {
        setTotalRows(reportedTotal);
      } else {
        setTotalRows(((page - 1) * pageSize) + rows.length + (data.hasMore ? 1 : 0));
      }

      if (rows.length > 0) {
        setLocationOptions((prev) => {
          const unique = new Set(prev);
          rows
            .map((row) => row.location)
            .filter((value) => typeof value === 'string' && value.trim() !== '' && value !== 'N/A')
            .forEach((value) => unique.add(value));
          return Array.from(unique).sort((a, b) => a.localeCompare(b));
        });
      }
    } catch (error) {
      console.error('Failed to load scores:', error);
      setAttempts([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [
    isAdmin,
    userCenter,
    selectedLocation,
    selectedAttendingFilter,
    selectedGenderFilter,
    sortField,
    sortDirection,
    debouncedSearch,
    listAttemptScores,
  ]);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      loadScoresPage(1, rowsPerPage, debouncedSearch);
      return;
    }
    setCurrentPage(1);
    loadScoresPage(1, rowsPerPage, debouncedSearch);
  }, [
    isAdmin,
    userCenter,
    selectedLocation,
    selectedAttendingFilter,
    selectedGenderFilter,
    rowsPerPage,
    debouncedSearch,
    sortField,
    sortDirection,
    loadScoresPage,
  ]);

  const columns = useMemo(() => {
    const baseColumns = [
      { name: 'Name', selector: row => row.name, sortable: true, sortKey: 'name' },
      { name: 'Email', selector: row => row.email, sortable: true, sortKey: 'email' },
      { name: 'Mobile', selector: row => row.mobile, sortable: true, sortKey: 'mobile' },
    ];

    if (isAdmin) {
      baseColumns.push({
        name: 'Location',
        selector: row => row.location,
        sortable: true,
        wrap: true,
        minWidth: '180px',
        sortKey: 'location',
      });
    }

    const endColumn = [
      { id: 'grandTotal', name: 'Grand Total (400)', selector: row => row.grandTotal || 0, sortable: true, sortKey: 'grandTotal' },
      { id: 'percentage', name: 'Percentage', selector: row => `${row.percentage}%`, sortable: true, sortKey: 'percentage' },
    ];

    const quizColumns = allQuizTitles.map((quiz) => ({
      name: quiz.title,
      selector: (row) => {
        const scores = row.quizScores || {};
        const candidates = [
          quiz.id,
          quiz.docId,
          quiz.id ? String(Number(quiz.id)) : null,
          quiz.docId ? String(Number(quiz.docId)) : null,
        ].filter(Boolean);
        for (const key of candidates) {
          if (scores[key] !== undefined && scores[key] !== null) {
            return scores[key];
          }
        }
        return "N/A";
      },
      sortable: true,
      wrap: true,
      minWidth: "150px",
      sortKey: `quiz:${quiz.id || quiz.docId}`,
    }));

    return [...baseColumns, ...quizColumns, ...endColumn];
  }, [allQuizTitles, isAdmin]);

  const filteredItems = attempts;
  const tableKey = useMemo(
    () => ['scores', isAdmin ? 'admin' : userCenter || 'center', ...allQuizTitles.map((quiz) => quiz.id || quiz.docId || quiz.title)].join('::'),
    [allQuizTitles, isAdmin, userCenter]
  );

  const handlePageChange = async (page) => {
    setCurrentPage(page);
    await loadScoresPage(page, rowsPerPage, debouncedSearch);
  };

  const handleRowsPerPageChange = async (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    await loadScoresPage(1, newRowsPerPage, debouncedSearch);
  };

  const handleSort = (column, direction) => {
    const nextSortField = column.sortKey || 'id';
    setSortField(nextSortField);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const exportFilteredScores = useCallback(async () => {
    setIsExporting(true);
    try {
      const allRows = [];
      let page = 1;
      let hasMore = true;
      const exportPageSize = 200;

      while (hasMore) {
        const response = await listAttemptScores({
          location: isAdmin ? selectedLocation : '',
          attendingMode: selectedAttendingFilter || '',
          gender: selectedGenderFilter || '',
          search: debouncedSearch || '',
          page,
          pageSize: exportPageSize,
          sortField,
          sortDirection,
        });

        const data = response.data || {};
        const rows = Array.isArray(data.rows) ? data.rows : [];
        allRows.push(...rows);

        hasMore = Boolean(data.hasMore);
        page += 1;
      }

      const csv = convertToCSV(allRows, columns);
      downloadCSV(csv, 'scores.csv');
    } catch (error) {
      console.error('Failed to export scores:', error);
      alert('Failed to export scores. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [
    listAttemptScores,
    isAdmin,
    selectedLocation,
    selectedAttendingFilter,
    selectedGenderFilter,
    debouncedSearch,
    sortField,
    sortDirection,
    columns,
  ]);

  const tableTitle = useMemo(() => {
    const label = !isAdmin
      ? `Scores from Center: ${userCenter}`
      : selectedLocation
        ? `Scores from Center: ${selectedLocation}`
        : 'Scores from All Centers';
    return (
      <div className="table-title">
        <span>{label}</span>
        <span className="location-count">{filteredItems.length} of {totalRows} records</span>
      </div>
    );
  }, [isAdmin, userCenter, selectedLocation, filteredItems.length, totalRows]);

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div className="table-header">
        <FilterComponent
          onFilter={e => setFilterText(e.target.value)}
          filterText={filterText}
          isAdmin={isAdmin}
          locations={locationOptions}
          selectedLocation={selectedLocation}
          onLocationChange={(e) => setSelectedLocation(e.target.value)}
          onLocationClear={() => setSelectedLocation('')}
          selectedAttendingFilter={selectedAttendingFilter}
          onAttendingFilterChange={(e) => setSelectedAttendingFilter(e.target.value)}
          selectedGenderFilter={selectedGenderFilter}
          onGenderFilterChange={(e) => setSelectedGenderFilter(e.target.value)}
        />
        <div className="table-actions">
          <button
            className="btn-export"
            onClick={exportFilteredScores}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>
    );
  }, [filterText, isAdmin, locationOptions, selectedLocation, selectedAttendingFilter, selectedGenderFilter, isExporting, exportFilteredScores]);

  return (
    <div className="dtq-locations-container">
      <DataTable
        key={tableKey}
        title={tableTitle}
        columns={columns}
        data={filteredItems}
        subHeader
        subHeaderComponent={subHeaderComponentMemo}
        persistTableHead
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handleRowsPerPageChange}
        paginationPerPage={rowsPerPage}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        sortServer
        defaultSortFieldId="grandTotal"
        defaultSortAsc={false}
        onSort={handleSort}
        highlightOnHover
        responsive
        progressPending={loading}
        progressComponent={<div className="loading-spinner">Loading scores...</div>}
        customStyles={customStyles}
        dense
      />
    </div>
  );
};

export default AttemptsTab;
